import { ethers } from 'ethers'

// Gold token address
const GOLD_TOKEN_ADDRESS = '0x2aB716338cF37B54B478E2898F7DBD75061C414d'

// Minimal ABI for whitelist checking
const GOLD_TOKEN_ABI = [
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "name": "whitelist",
    "outputs": [
      {
        "internalType": "bool",
        "name": "",
        "type": "bool"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "whitelistEnabled",
    "outputs": [
      {
        "internalType": "bool",
        "name": "",
        "type": "bool"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  }
]

// RPC endpoint for Poodl network
const RPC_URL = 'https://rpc.poodl.org'

// Cache for whitelist status
interface WhitelistCache {
  [address: string]: {
    isWhitelisted: boolean
    timestamp: number
  }
}

let whitelistCache: WhitelistCache = {}
let whitelistEnabledCache: { enabled: boolean; timestamp: number } | null = null
const CACHE_DURATION = 2 * 60 * 1000 // 2 minutes

/**
 * Checks if whitelist is enabled on the gold token contract
 */
export async function isWhitelistEnabled(): Promise<boolean> {
  try {
    const now = Date.now()
    
    // Check cache
    if (whitelistEnabledCache && (now - whitelistEnabledCache.timestamp) < CACHE_DURATION) {
      return whitelistEnabledCache.enabled
    }

    // Create provider and contract instance
    const provider = new ethers.providers.JsonRpcProvider(RPC_URL)
    const contract = new ethers.Contract(GOLD_TOKEN_ADDRESS, GOLD_TOKEN_ABI, provider)
    
    // Check if whitelist is enabled
    const enabled = await contract.whitelistEnabled()
    
    // Update cache
    whitelistEnabledCache = {
      enabled,
      timestamp: now
    }
    
    console.log('Whitelist enabled status:', enabled)
    return enabled
  } catch (error) {
    console.error('Error checking whitelist enabled status:', error)
    // Default to enabled (more restrictive) in case of error
    return true
  }
}

/**
 * Checks if an address is whitelisted in the gold token contract
 */
export async function isAddressWhitelisted(address: string): Promise<boolean> {
  if (!address) {
    return false
  }

  try {
    const normalizedAddress = address.toLowerCase()
    const now = Date.now()

    // Check cache
    if (whitelistCache[normalizedAddress] && (now - whitelistCache[normalizedAddress].timestamp) < CACHE_DURATION) {
      return whitelistCache[normalizedAddress].isWhitelisted
    }

    // Create provider and contract instance
    const provider = new ethers.providers.JsonRpcProvider(RPC_URL)
    const contract = new ethers.Contract(GOLD_TOKEN_ADDRESS, GOLD_TOKEN_ABI, provider)
    
    // Check whitelist status
    const isWhitelisted = await contract.whitelist(address)
    
    // Update cache
    whitelistCache[normalizedAddress] = {
      isWhitelisted,
      timestamp: now
    }
    
    console.log(`Address ${address} whitelist status:`, isWhitelisted)
    return isWhitelisted
  } catch (error) {
    console.error('Error checking whitelist status:', error)
    return false
  }
}

/**
 * Checks if a wallet can buy the gold token
 * Returns true if whitelist is disabled OR if the address is whitelisted
 */
export async function canBuyGoldToken(address: string): Promise<boolean> {
  if (!address) {
    return false
  }

  try {
    // First check if whitelist is enabled
    const whitelistEnabled = await isWhitelistEnabled()
    
    // If whitelist is disabled, anyone can buy
    if (!whitelistEnabled) {
      console.log('Whitelist is disabled, allowing purchase')
      return true
    }
    
    // If whitelist is enabled, check if address is whitelisted
    const isWhitelisted = await isAddressWhitelisted(address)
    console.log(`Whitelist enabled. Address ${address} can buy: ${isWhitelisted}`)
    return isWhitelisted
  } catch (error) {
    console.error('Error in canBuyGoldToken:', error)
    return false
  }
}

/**
 * Clears the whitelist cache (useful for testing or manual refresh)
 */
export function clearWhitelistCache(): void {
  whitelistCache = {}
  whitelistEnabledCache = null
  console.log('Whitelist cache cleared')
}

/**
 * Gets the gold token address
 */
export function getGoldTokenAddress(): string {
  return GOLD_TOKEN_ADDRESS
}
